import { ArrowUpRight } from 'lucide-react';
import { useT } from '@/i18n';

/**
 * Cluster H v2 Fase 2.7 — OUTBOUND referral card. Shown on ClientProfilePage
 * when ClientProfile.linkedHouseholdPromotions has 1+ entries. Mint palette
 * (success/positive — "this client's screening generated new clients") with
 * a border-l-4 mint-400 accent. Visually distinct from peach inbound.
 *
 * Each row is a clickable button that opens the child client in a
 * ClientProfileModal overlay. No <a> tags — modal-only navigation v1.
 */

export interface LinkedHouseholdPromotion {
  clientId: string;
  clientLastName: string;
  clientCinLast6: string;
  householdMemberId: string;
  promotedAt: string;
  promotedByAgentName: string | null;
}

export interface LinkedHouseholdPromotionsCardProps {
  promotions: LinkedHouseholdPromotion[];
  /** Opens a child client's profile in the ClientProfileModal overlay. */
  onItemClick: (clientId: string) => void;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function LinkedHouseholdPromotionsCard({
  promotions,
  onItemClick,
}: LinkedHouseholdPromotionsCardProps): JSX.Element | null {
  const t = useT();
  if (promotions.length === 0) return null;
  return (
    <div
      role="region"
      aria-label={t('linkedPromotions.title', { n: promotions.length })}
      className="rounded-md border border-mint-200 border-l-4 border-l-mint-400 bg-mint-50 p-3 space-y-2"
    >
      <div className="flex items-center gap-2">
        <ArrowUpRight className="h-5 w-5 text-mint-700 shrink-0" aria-hidden />
        <p className="text-xs font-semibold uppercase tracking-wide text-mint-800">
          {t('linkedPromotions.title', { n: promotions.length })}
        </p>
      </div>
      <ul className="space-y-1.5">
        {promotions.map((p) => {
          const label = p.clientCinLast6
            ? `${p.clientLastName} (CIN …${p.clientCinLast6})`
            : p.clientLastName;
          return (
            <li key={p.householdMemberId}>
              <button
                type="button"
                onClick={() => onItemClick(p.clientId)}
                className="w-full text-left flex items-center justify-between gap-2 px-2 py-1.5 rounded-sm hover:bg-mint-100 focus:outline-none focus:ring-2 focus:ring-mint-400 transition-colors"
              >
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-mint-900">
                  {label}
                </span>
                <span className="text-xs text-mint-800 shrink-0">
                  {fmtDate(p.promotedAt)}
                  {p.promotedByAgentName ? ` · ${p.promotedByAgentName}` : null}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
